// Staff softphone sign-in screen (roadmap #41), route /staff.
//
// There is no in-app credential form: the screen runs a silent single sign-on
// check on mount and either drops the staff on the waiting dashboard, redirects
// them to sign in, or (when the account is active on another device) asks them
// to confirm before signing the other device out. No technology names in
// user-facing text.
import { useEffect, useState } from 'react'
import BrandLockup from '../components/BrandLockup'
import { useNavigate } from 'react-router-dom'
import {
  isStaffAuthenticated,
  bootstrapStaffSession,
  staffSignIn,
  confirmOtherDeviceLogout,
  cancelPendingLogin,
} from './staffAuth'
import { t } from '../i18n'

const StaffLogin = () => {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [otherDevicePrompt, setOtherDevicePrompt] = useState(false)

  // ESTABLISH OR RESTORE THE SESSION VIA SINGLE SIGN-ON, THEN ROUTE ACCORDINGLY.
  useEffect(() => {
    if (isStaffAuthenticated()) {
      navigate('/staff/dashboard', { replace: true })
      return
    }
    let active = true
    ;(async () => {
      const result = await bootstrapStaffSession()
      if (!active) return
      if (result.ok) {
        navigate('/staff/dashboard', { replace: true })
      } else if (result.otherDeviceActive) {
        setOtherDevicePrompt(true)
      } else if (result.needsSignIn) {
        staffSignIn()
      } else {
        setError(result.message || t.loginFailedGeneric)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const onConfirmOtherDevice = async () => {
    setError('')
    setLoading(true)
    const result = await confirmOtherDeviceLogout()
    setLoading(false)
    setOtherDevicePrompt(false)
    if (result.ok) {
      navigate('/staff/dashboard', { replace: true })
    } else {
      setError(result.message || t.loginFailedGeneric)
    }
  }

  const onCancelOtherDevice = () => {
    cancelPendingLogin()
    setOtherDevicePrompt(false)
    staffSignIn()
  }

  return (
    <div className="cf-page">
      <div className="cf-card">
        <BrandLockup />
        <h1 className="cf-title">{t.staffLoginTitle}</h1>
        {error ? (
          <div className="cf-error cf-error-block">{error}</div>
        ) : (
          <p className="cf-subtitle">{t.staffRedirecting}</p>
        )}
      </div>

      {otherDevicePrompt && (
        <div className="cf-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cf-modal">
            <h2 className="cf-modal-title">{t.otherDeviceTitle}</h2>
            <p className="cf-modal-body">{t.otherDeviceBody}</p>
            <div className="cf-modal-actions">
              <button
                type="button"
                className="cf-btn cf-btn-secondary"
                onClick={onCancelOtherDevice}
                disabled={loading}
              >
                {t.otherDeviceCancel}
              </button>
              <button
                type="button"
                className="cf-btn cf-btn-primary"
                onClick={onConfirmOtherDevice}
                disabled={loading}
              >
                {loading ? t.signingIn : t.otherDeviceConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StaffLogin
